import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { buildFileDownloadUrl, deleteFileLibrary, getApiBaseUrl } from '../../utils/apiConfig';
import * as Icons from 'react-feather';

const isVideoFile = (mimeType = '', name = '') => {
  const lower = String(mimeType || '').toLowerCase();
  if (lower.startsWith('video/')) return true;
  return ['.mp4', '.mov', '.webm', '.mkv', '.avi'].some((ext) => String(name || '').toLowerCase().endsWith(ext));
};

const isImageFile = (mimeType = '', name = '') => {
  const lower = String(mimeType || '').toLowerCase();
  if (lower.startsWith('image/')) return true;
  return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp'].some((ext) => String(name || '').toLowerCase().endsWith(ext));
};

function VehicleMediaGrid({ vehicleId, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchRows = useCallback(async () => {
    if (!vehicleId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(getApiBaseUrl() + '/file/list', {
        entityId: vehicleId,
        entityType: 'vehicle'
      });
      const payload = res?.data?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.[0])
          ? payload[0]
          : [];

      const filtered = (list || []).filter((f) => String(f?.FileCategory || '').toLowerCase() === 'general');
      setRows(filtered);
    } catch (e) {
      setRows([]);
      setError('Unable to load media.');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows, refreshKey]);

  const previewUrl = useCallback((fileId) => buildFileDownloadUrl(fileId), []);

  const handleDelete = async (row) => {
    if (!row?.FileLibraryId) return;
    const ok = window.confirm('Delete this file?');
    if (!ok) return;
    try {
      const res = await deleteFileLibrary(row.FileLibraryId);
      if (res?.data?.diskDeleted === false) {
        setError('File could not be removed from disk. The record was kept.');
        return;
      }
      setError('');
      fetchRows();
    } catch (e) {
      setError('Unable to delete file.');
    }
  };

  const displayRows = useMemo(() => rows || [], [rows]);

  if (loading) return <div className="text-muted small">Loading…</div>;
  if (error) return <div className="text-danger small">{error}</div>;

  return (
    <div className="table-responsive" style={{ maxHeight: '360px', overflowY: 'auto' }}>
      <table className="table table-sm table-bordered align-middle mb-0">
        <thead>
          <tr>
            <th style={{ width: '60px' }}>#</th>
            <th style={{ width: '120px' }}>Preview</th>
            <th>File</th>
            <th style={{ width: '120px', textAlign: 'center' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {displayRows.length === 0 && (
            <tr>
              <td colSpan={4} className="text-muted small">No photos/videos uploaded.</td>
            </tr>
          )}
          {displayRows.map((row, idx) => {
            const url = previewUrl(row.FileLibraryId);
            const name = row?.OriginalName || row?.StoredName || `File-${row.FileLibraryId}`;
            const mime = row?.MimeType || '';
            const isVideo = isVideoFile(mime, name);
            const isImage = isImageFile(mime, name);
            return (
              <tr key={row.FileLibraryId || idx}>
                <td>{idx + 1}</td>
                <td>
                  {isImage ? (
                    <a href={url} target="_blank" rel="noreferrer">
                      <img
                        src={url}
                        alt={name}
                        style={{ width: '96px', height: '64px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    </a>
                  ) : isVideo ? (
                    <video
                      src={url}
                      style={{ width: '96px', height: '64px', objectFit: 'cover', borderRadius: '4px' }}
                      muted
                      controls
                      playsInline
                    />
                  ) : (
                    <a href={url} target="_blank" rel="noreferrer" className="small">Open</a>
                  )}
                </td>
                <td>
                  <div className="small">{name}</div>
                  {mime ? <div className="text-muted small">{mime}</div> : null}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-danger"
                    onClick={() => handleDelete(row)}
                    title="Delete"
                    style={{ textDecoration: 'none' }}
                  >
                    <Icons.Trash2 size={16} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default VehicleMediaGrid;

